/*
 *  Copyright (c) 2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { isApolloError } from "@apollo/client";
import { useAuth0 } from "@auth0/auth0-react";
import { useLocation } from "react-use";
import {
  EstateV3ExportMutationVariables,
  useEstateV3ExportMutation,
} from "~/generated/graphql";
import { SUCCESS_TEXT } from "~/constants";
import { showSimpleInfo, showSuccess } from "~/services/toast";
import { handleError, toInputError } from "~/tools";

type Filters = EstateV3ExportMutationVariables["input"]["filters"];

export function useEstateRecordsExport(callback: () => void) {
  const [estateExport, mutationState] = useEstateV3ExportMutation();
  const { user } = useAuth0();
  const { href } = useLocation();

  return [
    async (filters: Filters) => {
      try {
        await estateExport({
          variables: {
            input: {
              filters,
              viewUrl: href?.replace(/page=\d+&/, "page=0&") ?? "",
              notificationEmail: user?.email ?? "",
            },
          },
        });
        showSuccess(SUCCESS_TEXT.export, false);
        callback();
      } catch (err) {
        const error = toInputError(err);
        const isExportInProgress =
          isApolloError(error) &&
          error.graphQLErrors.some(
            (graphQlError) =>
              graphQlError.extensions.code === "EXPORT_IN_PROGRESS"
          );

        if (isExportInProgress) {
          showSimpleInfo(
            "Similar export action recently requested. Please retry later.",
            false
          );
        } else {
          handleError(error);
        }
        callback();
      }
    },
    mutationState,
  ] as const;
}
